import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import {
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { ExpensesService } from '../../features/expenses/services/expenses.service';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import { AddExpenseDialogComponent } from '../expenses/add-expense-dialog.component';
import { PermissionService } from '../../shared/services/permission.service';
import { ModuleIds } from '../../shared/permission-constants/permission-const';

@Component({
  selector: 'app-expense-details',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    MatTabsModule,
    MatMenuModule,
    MatIconModule,
    MatSelectModule,
    ReactiveFormsModule,
    FormsModule,
  ],
  templateUrl: './expense-details.component.html',
  styleUrl: './expense-details.component.scss',
})
export class ExpenseDetailsComponent implements OnInit {
  selectedTabIndex = 0;
  expenseId: any;
  expenseData: any = {};
  dataExpense: any = {};
  allExpenses: any[] = [];
  comments: any;
  journalData: any;
  public loading: boolean = true;
  private searchSubject = new Subject<string>();
  isAttacheFile = false;
  isDialogOpen = false; // Flag to prevent multiple dialog openings
  canApproveRejectExpenses$ = this.permissionService.hasPermission(
    ModuleIds.Expenses,
    'Approve/Reject'
  );
  canListExpenses$ = this.permissionService.hasPermission(
    ModuleIds.Expenses,
    'List'
  );
  canCreateExpenses$ = this.permissionService.hasPermission(
    ModuleIds.Expenses,
    'Create'
  );
canEditExpenses$ = this.permissionService.hasPermission(
    ModuleIds.Expenses,
    'Edit'
  );
  canDeleteExpenses$ = this.permissionService.hasPermission(
    ModuleIds.Expenses,
    'Delete'
  );

  constructor(
    private router: Router,
    private expensesService: ExpensesService,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private permissionService: PermissionService
  ) {}

  ngOnInit(): void {
    // First check for ID in path parameter (e.g., /expense-details/123)
    this.route.params.subscribe((params) => {
      const pathId = params['id'];
      if (pathId) {
        this.expenseId = pathId;
        this.getExpenseById(this.expenseId);
      }
    });

    // Then check for expenseId in query parameters
    this.route.queryParamMap.subscribe((queryParams) => {
      const queryExpenseId = queryParams.get('expenseId');
      if (queryExpenseId && !this.expenseId) {
        this.expenseId = queryExpenseId;
        this.getExpenseById(this.expenseId);
      }
      this.fetchAllExpenses(); // move this inside to ensure sync
    });
  
    this.searchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.fetchAllExpenses(searchTerm);
      });
  }

  navigateTo(route: string) {
    this.router.navigate([route]);
  }

  openCreateDialog() {
    console.log('=== OPENING CREATE DIALOG FROM DETAILS ===');
    console.log('Is dialog already open?', this.isDialogOpen);
    
    if (this.isDialogOpen) {
      console.log('Dialog is already open, preventing duplicate opening');
      return;
    }
    
    this.isDialogOpen = true;
    const dialogRef = this.dialog.open(AddExpenseDialogComponent, {
      disableClose: true,
      data: null, // null data means create mode
      width: '90vw',
      maxWidth: '1200px',
      height: '90vh'
    });

    dialogRef.afterClosed().subscribe((result) => {
      console.log('Create dialog closed with result:', result);
      this.isDialogOpen = false; // Reset the flag when dialog closes
      console.log('Dialog flag reset to false');
      
      if (result && result.success) {
        console.log('Expense created successfully:', result.data);
        console.log('Refreshing expense list...');
        
        // Refresh the expense list to include the new expense
        this.fetchAllExpenses();
        this.toastr.success('Expense created successfully!');
        
        // Automatically select and show the newly created expense
        if (result.data && result.data.id) {
          console.log('Newly created expense ID:', result.data.id);
          
          // Update the expense ID and fetch the new expense data
          this.expenseId = result.data.id;
          this.getExpenseById(result.data.id);
          
          // Update URL to reflect the new expense
          this.router.navigate(['expenses/expense-details', result.data.id], {
            queryParams: { expenseId: result.data.id },
          });
          
          // Find and select the new expense in the tab list
          setTimeout(() => {
            const newExpenseIndex = this.allExpenses.findIndex(expense => expense.id === result.data.id);
            if (newExpenseIndex !== -1) {
              this.selectedTabIndex = newExpenseIndex;
              console.log('New expense selected at index:', newExpenseIndex);
            }
          }, 500); // Small delay to ensure list is refreshed
        }
      } else {
        console.log('Dialog closed without success or result is null');
      }
    });
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;
    this.searchSubject.next(searchTerm);
  }

  fetchAllExpenses(search: string = '') {
    this.loading = true;
    const input = {
      search: search
    }
    this.expensesService.findAllExpensesWithPagination(input).subscribe({
      next: (expenses) => {
        this.allExpenses = expenses?.expenses || [];

        if (this.expenseId) {
          const targetId = +this.expenseId;
          const index = this.allExpenses.findIndex(
            (c: any) => c.id === targetId
          );
          this.selectedTabIndex = index !== -1 ? index : 0;
          this.expenseData = this.allExpenses[this.selectedTabIndex] || {};
          this.getExpenseById(targetId); // ensure latest data
        } else {
          this.selectedTabIndex = 0;
          this.expenseData = this.allExpenses[0] || {};
        }
      },
      error: (err) => {
        console.error('Error fetching expenses:', err);
      },
    });
  }
  

  getExpenseById(id: any) {
    this.expensesService.getExpenseById(Number(id)).subscribe({
      next: (data) => {
        this.expenseData = data;
        this.journalData = this.expenseData.journalData;
      },
      error: (err)=>{
        console.log(err);
      }
    });
  }

  selectTab(index: number) {
    this.selectedTabIndex = index;
    const selectedExpense = this.allExpenses[index];
    
    if (selectedExpense) {
      this.expenseId = selectedExpense.id;
      this.getExpenseById(selectedExpense.id);
      
      // Update URL with the selected expense ID
      this.router.navigate(['expenses/details', selectedExpense.id]);
    }
  }

  isEditableStatus(): boolean {
    const status = this.expenseData?.expenseStatus;
    return status !== 'Approved' && status !== 'Rejected' && status !== 'Sent';
  }

  isDeletableStatus(): boolean {
    const status = this.expenseData?.expenseStatus;
    return status === 'Draft' || status === 'Pending';
  }

  getDebitTotal(): number {
    return (
      this.expenseData?.journalData?.journalEntries?.reduce(
        (sum: number, entry: any) => sum + (entry.debit || 0),
        0
      ) || 0
    );
  }

  getCreditTotal(): number {
    return (
      this.expenseData?.journalData?.journalEntries?.reduce(
        (sum: number, entry: any) => sum + (entry.credit || 0),
        0
      ) || 0
    );
  }

  navigateToEditPage(): void {
    this.openEditDialog(this.expenseData);
  }

  openEditDialog(expenseData: any) {
    console.log('=== OPENING EDIT DIALOG FROM DETAILS ===');
    console.log('Expense data:', expenseData);
    console.log('Is dialog already open?', this.isDialogOpen);
    
    if (this.isDialogOpen) {
      console.log('Dialog is already open, preventing duplicate opening');
      return;
    }
    
    this.isDialogOpen = true;
    const dialogRef = this.dialog.open(AddExpenseDialogComponent, {
      disableClose: true,
      data: expenseData,
      width: '90vw',
      maxWidth: '1200px',
      height: '90vh'
    });

    dialogRef.afterClosed().subscribe((result) => {
      console.log('Edit dialog closed with result:', result);
      this.isDialogOpen = false; // Reset the flag when dialog closes
      console.log('Dialog flag reset to false');
      
      if (result && result.success) {
        console.log('Expense updated successfully:', result.data);
        console.log('Refreshing expense data...');
        // Refresh the current expense data
        this.getExpenseById(this.expenseId);
        // Refresh the expense list
        this.fetchAllExpenses();
        this.toastr.success('Expense updated successfully!');
      } else {
        console.log('Dialog closed without success or result is null');
      }
    });
  }

  onRemoveExpense(){
    if (!this.isDeletableStatus()) {
      this.toastr.error('Only Draft and Pending expenses can be deleted.');
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialog, {
      data: {
        title: 'Confirm Delete',
        message: 'Are you sure you want to delete this expense?',
        confirmText: 'Delete'
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.removeExpense(this.expenseId);
      }
    });
  }

  private removeExpense(expenseId: number): void {
    this.expensesService.removeExpense(Number(expenseId)).subscribe({
      next: () => {
        this.toastr.success('Expense deleted successfully.');
        this.router.navigate([`expenses`]);
      },
      error: (error: any) => {
        this.toastr.error(
          error.message || 'Failed to delete expense. Please try again.'
        );
      },
    });
  }

  handleApproval(isApproved: boolean, expenseId: any): void {
    const dialogRef = this.dialog.open(ConfirmDialog, {
      data: {
        title: isApproved ? 'Confirm Approval' : 'Confirm Rejection',
        message: isApproved
          ? 'Are you sure you want to approve this expense?'
          : 'Are you sure you want to reject this expense?',
        confirmText: 'Confirm',
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        if (isApproved) {
          const input = {
            id: Number(expenseId),
            expenseStatus : 'Approved'
          }
          this.expensesService.approveOrRejectExpense(Number(expenseId), input).subscribe({
            next: (res) => {
              console.log('Expense approved:', res);
              this.toastr.success('Expense status successfully updated')
              this.fetchAllExpenses();
              this.getExpenseById(this.expenseId);
            },
            error: (err) => {
              console.error('Error approving expense:', err);
            },
          });
        } else {
          const input = {
            id: Number(expenseId),
            expenseStatus : 'Rejected'
          }
          this.expensesService.approveOrRejectExpense(Number(expenseId), input).subscribe({
            next: (res) => {
              console.log('Expense rejected:', res);
              this.toastr.success('Expense status successfully updated')
              this.fetchAllExpenses();
              this.getExpenseById(this.expenseId);
            },
            error: (err) => {
              console.error('Error rejecting expense:', err);
            },
          });
        }
      }
    });
  }

  readonly dialog = inject(MatDialog);

  isDropdownOpen = false;
  isCommentsOpen = false;

  markAsSent(id: any) {
    const input = {
      id: Number(id),
      expenseStatus : 'Sent'
    }
    this.expensesService.approveOrRejectExpense(Number(id), input).subscribe({
      next: (res)=>{
        this.toastr.success('Expense status updated')
      },
      error: (err)=>{
        this.toastr.error(err.message || 'Failed to updated expense')
      }
    })
  }


  markAsRequest() {
    if (!this.expenseData?.id) {
      this.toastr.error('No expense selected');
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialog, {
      data: {
        title: 'Confirm Mark as Request',
        message: 'Are you sure you want to mark this draft expense as a request?',
        confirmText: 'Confirm',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        // Update the expense status to "Pending" to mark it as a request
        const updateData = {
          ...this.expenseData,
          expenseStatus: 'Pending'
        };

        this.expensesService.updateExpense(this.expenseData.id, updateData).subscribe({
          next: (res: any) => {
            console.log('Expense marked as request:', res);
            this.toastr.success('Expense marked as request successfully!');
            // Refresh the expense data
            this.getExpenseById(this.expenseData.id);
            this.fetchAllExpenses();
          },
          error: (err: any) => {
            console.error('Error marking expense as request:', err);
            this.toastr.error('Failed to mark expense as request. Please try again.');
          },
        });
      }
    });
  }
} 